import type { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'

import { ChevronDownIcon } from 'src/shared/components/icons/chevron-down-icon'
import { cn } from 'src/shared/utils/cn'

interface ImageCarouselProps {
  images: (string | undefined | null)[]
  imageContainerClassName?: string
  className?: string
  showSideArrows?: boolean
  loop?: boolean
  startIndex?: number
  dotContainerClassName?: string
}

/** Image carousel component for displaying a list of images. */
export default function ImageCarousel({
  images,
  className,
  imageContainerClassName,
  showSideArrows = false,
  loop = true,
  startIndex = 0,
  dotContainerClassName,
}: ImageCarouselProps) {
  const filteredImages = images.filter((image) => image !== undefined && image !== null)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    align: 'center',
    skipSnaps: false,
    startIndex,
  })

  const [selectedIdx, setSelectedIdx] = useState(0)

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIdx(emblaApi.selectedScrollSnap())
  }, [])

  const onNext = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  const onPrev = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) {
      return
    }

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <div className={cn('relative overflow-hidden rounded-[10px]', className)}>
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex gap-[20px]">
          {filteredImages.map((image, index) => (
            <div className={cn('h-[260px] w-full flex-shrink-0', imageContainerClassName)} key={index}>
              <img src={image} alt={'carousel image'} className="size-full object-cover" />
            </div>
          ))}
        </div>
      </div>
      <div
        className={cn(
          'absolute bottom-0 left-0 right-0 flex flex-row items-center justify-center gap-2 bg-gradient-to-b from-black/0 to-black/45 px-5 py-[13px]',
          dotContainerClassName,
        )}
      >
        {filteredImages &&
          filteredImages.length > 1 &&
          Array.from({ length: filteredImages.length }).map((_, index) => (
            <div
              className={cn(
                'size-[6px] flex-shrink-0 rounded-full bg-white',
                Math.abs(selectedIdx - index) > 2 && 'size-[5px]',
                selectedIdx !== index && 'opacity-60',
              )}
              key={index}
            ></div>
          ))}
      </div>
      {showSideArrows && filteredImages.length > 1 && (
        <>
          {/* Previous button */}
          <div className="absolute left-[45px] top-1/2 translate-y-[-50%]">
            <div
              className={cn(
                'flex size-[61px] flex-shrink-0 items-center justify-center rounded-full bg-white hover:cursor-pointer',
                !loop && selectedIdx === 0 && 'opacity-50',
              )}
              onClick={onPrev}
            >
              <ChevronDownIcon className="size-6 rotate-90 text-primary" />
            </div>
          </div>

          {/* Next button */}
          <div className="absolute right-[45px] top-1/2 translate-y-[-50%]">
            <div
              className={cn(
                'flex size-[61px] flex-shrink-0 rotate-180 items-center justify-center rounded-full bg-white hover:cursor-pointer',
                !loop && selectedIdx === filteredImages.length - 1 && 'opacity-50',
              )}
              onClick={onNext}
            >
              <ChevronDownIcon className="size-6 rotate-90 text-primary" />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
